import { AxiosResponse } from 'axios';
import { Config } from '../config';
import { LoginResponse } from '../types/authTypes';
import apiClient from './apiClient';

export const getAdminStories = async (params: {
  email: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.getUserStories,
    params
  );
  return response;
};

export const getUserInDevsStories = async (params: {
  email: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.getUserInDevsStories,
    params
  );
  return response;
};

export const getApprovedRejectStories = async (params: {
  email: string;
  approverEmail: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.getApprovedRejectStories,
    params
  );
  return response;
};

export const updateStoryStatus = async (params: {
  email: string;
  storyId: string;
  status: string;
  approverEmail: string;
  approverName: string;
}): Promise<AxiosResponse> => {
  //const validStatuses = ["pending", "editing", "reviewed", "approved", "rejected"];
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.updateStoryStatus,
    params
  );
  return response;
};

export const createStory = async (params: {
  briefingId: string;
  briefingUrl: string;

  twitter: string;
  secondTwitter: string;

  topic: string[];
  imageUrl: string;
  status: string;
  //content
  heading: string;
  subHeading: string;
  content: string;

  type: string;
  storyType: string;
  email: string;
  name: string;
  // Guest byline override — omit to publish under the creator's own identity.
  authorName?: string;
  authorImageUrl?: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.createStory,
    params
  );
  return response;
};

export interface PresenceHolder {
  email: string;
  name: string;
  activity: string;
}

// Editorial soft-lock heartbeat. Fails soft (null = nobody else holds the
// story / presence unavailable) so the editor never blocks on it.
export const updateStoryPresence = async (params: {
  storyId: string;
  email: string;
  name: string;
  action: 'claim' | 'release';
  activity: 'editing' | 'reviewing';
}): Promise<PresenceHolder | null> => {
  try {
    const response = await apiClient.instance.post(
      Config.endPoint.updateStoryPresence,
      params
    );
    return response.data?.heldBy || null;
  } catch (error) {
    console.error('Story presence update failed', error);
    return null;
  }
};

export interface GuestAuthor {
  id: string;
  name: string;
  imageUrl?: string;
}

// Registry of past guest authors, for the "previous guests" dropdown in the
// story editor. Fails soft (empty list) so the editor still works if the
// endpoint is unreachable.
export const getGuestAuthors = async (): Promise<GuestAuthor[]> => {
  try {
    const response = await apiClient.instance.post(
      Config.endPoint.getGuestAuthors,
      {}
    );
    return response.data?.data || [];
  } catch (error) {
    console.error('Failed to fetch guest authors', error);
    return [];
  }
};

// Lean update used by the "make lead" action: only flips a story's type.
// briefingId must be included — the backend editStory always rewrites it.
export const updateStoryType = async (params: {
  storyId: string;
  briefingId: string;
  email: string;
  type: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.editStory,
    params
  );
  return response;
};

export const updateStory = async (params: {
  storyId: string;
  briefingId: string;
  briefingUrl: string;

  twitter: string;
  secondTwitter: string;

  topic: string[];
  imageUrl: string;
  status: string;
  //content
  heading: string;
  subHeading: string;
  content: string;

  type: string;
  storyType: string;
  email: string;
  name: string;
  // Conflict guard: the story's contentUpdatedAt (seconds) as loaded into
  // the editor, null if never saved. The backend rejects the save (409) if
  // the story changed in between.
  expectedContentUpdatedAt: number | null;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.editStory,
    params
  );
  return response;
};

export const deleteStory = async (params: {
  storyId: string;
  email: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.deleteStory,
    params
  );
  return response;
};

export const getStories = async (params: {
  email: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.getStories,
    params
  );
  return response;
};

export const getStoriesByBriefingId = async (params: {
  briefingId: string;
  email: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.getStoriesByBriefingId,
    params
  );
  return response;
};

export const fetchStoryDetails = async (params: {
  email: string;
  storyId: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.instance.post<LoginResponse>(
    Config.endPoint.getStoryDetails.replace('{storyId}', params.storyId),
    params
  );
  return response;
};
